/**
 * product-custom-variants.js
 *
 * Smart cascading variant selection for a 3-option product:
 *   Option 1 (index 0) — Panel Type   (e.g. "1-Panel" / "3-Panels")
 *   Option 2 (index 1) — Size         (e.g. "24x18")
 *   Option 3 (index 2) — Depth        (e.g. '1.5"')
 *
 * ═══════════════════════════════════════════════════════
 *  HOW THE CASCADING INTERCEPTOR WORKS
 * ═══════════════════════════════════════════════════════
 *
 *  A document-level listener registered with { capture: true } fires
 *  BEFORE Dawn's VariantSelects bubble-phase listener processes any
 *  option change.  This means we can fix downstream radios in-place
 *  and VariantSelects will read the already-corrected state — so it
 *  sends ONE AJAX request with a fully valid combo.
 *
 *  Result: price, URL, Add to Cart, and gallery all update in a
 *  single round-trip with zero flash of "Sold Out / Unavailable".
 *
 * ═══════════════════════════════════════════════════════
 *  CASCADE RULES
 * ═══════════════════════════════════════════════════════
 *
 *  When ANY option at index N changes:
 *   1. Options 0 … N are fixed (the user's intention).
 *   2. Options N+1 … end are "downstream" and may need correction.
 *   3. Try to keep current downstream values (exact match first).
 *   4. If unavailable, relax the LAST downstream option, then the
 *      second-to-last, etc., until an available combo is found.
 *
 *  Examples:
 *
 *  Panel → "3-Panels", current Size="48x36", current Depth='1.5"':
 *    try ["3-Panels","48x36","1.5\""] → available? keep everything ✓
 *    try ["3-Panels","48x36",  null ] → keep Size, find first Depth ✓
 *    try ["3-Panels",  null,   null ] → find first Size + Depth ✓
 *
 *  Size → "24x18", Panel stays "3-Panels", current Depth='1.5"':
 *    try ["3-Panels","24x18","1.5\""] → available? keep Depth ✓
 *    try ["3-Panels","24x18",  null ] → find first Depth ✓
 *
 *  Depth changes: no downstream → no cascade, Dawn handles normally.
 *
 * ═══════════════════════════════════════════════════════
 *  GALLERY ORDER  (main list + thumbnail strip)
 * ═══════════════════════════════════════════════════════
 *
 *  1-Panel selected:
 *    Pos 1  → first 1-panel image
 *    Pos 2  → metafield variant image (injected)
 *    Pos 3+ → remaining 1-panel images
 *    Then   → neutral slides  (always visible)
 *    Hidden → all 3-panel images
 *
 *  3-Panels selected:
 *    Pos 1  → first 3-panel image
 *    Pos 2  → metafield variant image (injected)
 *    Pos 3+ → remaining 3-panel images
 *    Then   → neutral slides  (always visible)
 *    Hidden → all 1-panel images
 */

(function () {
  'use strict';

  /* ───────────────────────────────────────────────────────
     CONSTANTS
  ─────────────────────────────────────────────────────── */

  var PANEL_1              = '1-panel';
  var PANEL_3              = '3-panel';
  var METAFIELD_MEDIA_ID   = 'custom-metafield-variant';
  var METAFIELD_SLIDE_ID   = 'Slide-custom-variant-metafield';
  var METAFIELD_THUMB_ID   = 'Slide-Thumbnails-custom-variant-metafield';
  var METAFIELD_MODAL_SLIDE_ID = 'Slide-ProductModal-custom-variant-metafield';
  var METAFIELD_MODAL_THUMB_ID = 'Slide-Thumbnails-Modal-custom-variant-metafield';
  var LEGACY_METAFIELD_MODAL_IMG_ID = 'custom-variant-metafield-modal-img';

  /**
   * Guard flag — set to true while we are programmatically updating
   * option inputs so that re-entrant interceptor calls are ignored.
   */
  var _selectingAvailable = false;

  /**
   * Debounce token for updateGallery.
   * Multiple event listeners (variant:change, change on #id input,
   * variant-radios change) can all fire within the same JS tick for a
   * single user interaction.  We collapse them to ONE execution per tick
   * so removeInjected + reorderList run exactly once — preventing the
   * duplicate slide that appeared when two listeners injected the same
   * metafield slide back-to-back.
   */
  var _galleryUpdateTimer    = null;
  var _lastGalleryVariantId  = null;

  function scheduleGalleryUpdate(variantId) {
    var id = String(variantId);
    _lastGalleryVariantId = id;
    if (_galleryUpdateTimer) clearTimeout(_galleryUpdateTimer);
    _galleryUpdateTimer = setTimeout(function () {
      _galleryUpdateTimer = null;
      updateGallery(_lastGalleryVariantId);
    }, 0);
  }

  /* ───────────────────────────────────────────────────────
     GENERAL UTILITIES
  ─────────────────────────────────────────────────────── */

  /** Lowercase + collapse whitespace. Used for all value comparisons. */
  function norm(str) {
    return (str || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  /**
   * Given a string, return PANEL_1 / PANEL_3 / null.
   * Strips dashes before checking so "1-Panel" and "1Panel" both match.
   */
  function detectPanel(str) {
    var n = norm(str).replace(/-/g, '');
    if (n.indexOf('1panel') !== -1) return PANEL_1;
    if (n.indexOf('3panel') !== -1) return PANEL_3;
    return null;
  }

  function show(el) { if (el) el.style.display = ''; }
  function hide(el) { if (el) el.style.display = 'none'; }

  function getSectionId() {
    var g = document.querySelector('media-gallery[id^="MediaGallery-"]');
    return g ? g.id.replace('MediaGallery-', '') : null;
  }

  function esc(str) {
    return (str || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /** Walk up the DOM tree looking for an element with a given tag name. */
  function ancestorByTag(el, tagName) {
    var node = el && el.parentNode;
    while (node) {
      if (node.tagName && node.tagName.toLowerCase() === tagName) return node;
      node = node.parentNode;
    }
    return null;
  }

  /* ───────────────────────────────────────────────────────
     GALLERY HELPERS
  ─────────────────────────────────────────────────────── */

  /**
   * Detect panel keyword from a gallery <li> element.
   * Priority: data-media-alt → img[alt] → button[aria-label] → [title]
   */
  function getPanelKw(li) {
    var da = li.getAttribute('data-media-alt') || '';
    if (da) { var k0 = detectPanel(da); if (k0) return k0; }

    var imgs = li.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      var k1 = detectPanel(imgs[i].getAttribute('alt') || '');
      if (k1) return k1;
    }

    var btns = li.querySelectorAll('button[aria-label]');
    for (var b = 0; b < btns.length; b++) {
      var k2 = detectPanel(btns[b].getAttribute('aria-label') || '');
      if (k2) return k2;
    }

    var ts = li.querySelectorAll('[title]');
    for (var t = 0; t < ts.length; t++) {
      var k3 = detectPanel(ts[t].getAttribute('title') || '');
      if (k3) return k3;
    }

    return null;
  }

  function removeInjected() {
    [
      METAFIELD_SLIDE_ID,
      METAFIELD_THUMB_ID,
      METAFIELD_MODAL_SLIDE_ID,
      METAFIELD_MODAL_THUMB_ID,
      LEGACY_METAFIELD_MODAL_IMG_ID,
    ].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.remove();
    });
  }

  /** Detect zoom mode ('hover' or 'click') from existing gallery toggle buttons. */
  function getZoomMode() {
    var toggle = document.querySelector('.product__media-toggle');
    if (toggle && toggle.classList.contains('product__media-zoom-hover')) return 'hover';
    return 'click';
  }

  /** Clone the zoom SVG from an existing gallery icon, or return an empty span. */
  function getZoomIconHtml() {
    var svgWrapper = document.querySelector('.product__media-icon .svg-wrapper');
    return svgWrapper ? svgWrapper.outerHTML : '<span class="svg-wrapper"></span>';
  }

  /**
   * Inject a matching <img data-media-id> into the product modal so that
   * ProductModal.showActiveMedia() can find it when the lightbox opens.
   */
  function injectModalImage(sectionId, imageUrl, altText) {
    var modal = document.getElementById('ProductModal-' + sectionId);
    if (!modal) return;
    var slider = modal.querySelector('#Slider-ProductModal-' + sectionId);
    if (!slider) return;

    var li = document.createElement('li');
    li.id        = METAFIELD_MODAL_SLIDE_ID;
    li.className = 'product-media-modal__media-item grid__item slider__slide';
    li.setAttribute('data-media-id', METAFIELD_MEDIA_ID);
    li.setAttribute('data-modal-slide', '');
    li.setAttribute('data-media-alt', altText || '');
    li.setAttribute('data-custom-metafield', 'true');
    li.innerHTML =
      '<img class="global-media-settings global-media-settings--no-shadow"' +
      ' src="' + esc(imageUrl) + '"' +
      ' alt="' + esc(altText) + '"' +
      ' loading="lazy"' +
      ' data-media-id="' + esc(METAFIELD_MEDIA_ID) + '"' +
      '>';

    // Insert at position 2 (after first visible slide)
    if (slider.children.length > 0) slider.insertBefore(li, slider.children[1] || null);
    else slider.appendChild(li);

    var thumbList = modal.querySelector('#Slider-Thumbnails-Modal-' + sectionId);
    if (thumbList) {
      var tli = document.createElement('li');
      tli.id        = METAFIELD_MODAL_THUMB_ID;
      tli.className = 'thumbnail-list__item slider__slide';
      tli.setAttribute('data-media-id', METAFIELD_MEDIA_ID);
      tli.setAttribute('data-media-alt', altText || '');
      tli.setAttribute('data-custom-metafield', 'true');
      tli.innerHTML =
        '<button type="button" class="thumbnail global-media-settings global-media-settings--no-shadow"' +
        ' aria-label="' + esc(altText || 'Variant image') + '"' +
        ' aria-controls="Slider-ProductModal-' + esc(sectionId) + '"' +
        ' data-modal-thumb data-media-id="' + esc(METAFIELD_MEDIA_ID) + '">' +
        '<img src="' + esc(imageUrl) + '" alt="' + esc(altText) + '" loading="lazy"' +
        ' width="74" height="74" style="object-fit:contain;width:100%;height:100%;">' +
        '</button>';

      if (thumbList.children.length > 0) thumbList.insertBefore(tli, thumbList.children[1] || null);
      else thumbList.appendChild(tli);
    }

    var modalViewer = document.getElementById('ProductModalViewer-' + sectionId);
    if (modalViewer && typeof modalViewer.resetPages === 'function') modalViewer.resetPages();
    var modalThumbs = document.getElementById('ProductModalThumbnails-' + sectionId);
    if (modalThumbs && typeof modalThumbs.resetPages === 'function') modalThumbs.resetPages();
  }

  function buildMainSlide(sectionId, imageUrl, altText) {
    var fullMediaId = sectionId + '-' + METAFIELD_MEDIA_ID;  // for <li> data-media-id
    var zoomMode    = getZoomMode();
    var iconHtml    = getZoomIconHtml();

    var li = document.createElement('li');
    li.id        = METAFIELD_SLIDE_ID;
    li.className = 'product__media-item grid__item slider__slide';
    li.setAttribute('data-media-id',  fullMediaId);
    li.setAttribute('data-media-alt', altText || '');
    li.setAttribute('data-custom-metafield', 'true');

    // Mirror the structure of product-thumbnail.liquid so the modal-opener
    // custom element wires the click → ProductModal.show(button) automatically.
    li.innerHTML =
      '<div class="product-media-container media-type-image global-media-settings gradient">' +
        '<modal-opener class="product__modal-opener product__modal-opener--image"' +
                      ' data-modal="#ProductModal-' + esc(sectionId) + '">' +
          '<span class="product__media-icon motion-reduce quick-add-hidden' +
                ' product__media-icon--' + zoomMode + '" aria-hidden="true">' +
            iconHtml +
          '</span>' +
          '<div class="product__media media media--transparent"' +
               ' style="width:100%;padding-bottom:100%;position:relative;overflow:hidden;">' +
            '<img src="' + esc(imageUrl) + '" alt="' + esc(altText) + '" loading="lazy"' +
                 ' class="image-magnify-' + zoomMode + '"' +
                 ' style="position:absolute;top:0;left:0;width:100%;height:100%;object-fit:contain;">' +
          '</div>' +
          '<button class="product__media-toggle quick-add-hidden product__media-zoom-' + zoomMode + '"' +
                  ' type="button" aria-haspopup="dialog"' +
                  ' data-media-id="' + esc(METAFIELD_MEDIA_ID) + '">' +
            '<span class="visually-hidden">Open media</span>' +
          '</button>' +
        '</modal-opener>' +
      '</div>';

    // For hover-zoom mode, manually attach the magnify listener since
    // magnify.js's enableZoomOnHover() already ran at page load.
    if (zoomMode === 'hover') {
      var img = li.querySelector('img');
      if (img && typeof magnify === 'function' && typeof moveWithHover === 'function') {
        img.onclick = function (event) {
          magnify(img, 2);
          moveWithHover(img, event, 2);
        };
      }
    }

    return li;
  }

  function buildThumbSlide(sectionId, imageUrl, altText) {
    var mediaId = sectionId + '-' + METAFIELD_MEDIA_ID;
    var li = document.createElement('li');
    li.id        = METAFIELD_THUMB_ID;
    li.className = 'thumbnail-list__item slider__slide';
    li.setAttribute('data-target',           mediaId);
    li.setAttribute('data-media-position',   '2');
    li.setAttribute('data-custom-metafield', 'true');
    li.innerHTML =
      '<button class="thumbnail global-media-settings global-media-settings--no-shadow"' +
               ' aria-label="' + esc(altText || 'Variant image') + '"' +
               ' aria-controls="GalleryViewer-' + sectionId + '">' +
        '<img src="' + esc(imageUrl) + '" alt="' + esc(altText) + '" loading="lazy"' +
             ' width="74" height="74" style="object-fit:contain;width:100%;height:100%;">' +
      '</button>';
    li.querySelector('button').addEventListener('click', function () {
      activateSlide(sectionId, mediaId, li);
    });
    return li;
  }

  function activateSlide(sectionId, mediaId, thumbLi) {
    var mainList = document.getElementById('Slider-Gallery-' + sectionId);
    if (mainList) {
      mainList.querySelectorAll('.slider__slide').forEach(function (s) {
        var active = s.getAttribute('data-media-id') === mediaId;
        s.classList.toggle('is-active', active);
        if (active) s.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      });
    }

    var thumbList = document.getElementById('Slider-Thumbnails-' + sectionId);
    if (thumbList) {
      thumbList.querySelectorAll('button.thumbnail').forEach(function (btn) {
        btn.removeAttribute('aria-current');
      });
      if (thumbLi) {
        var btn = thumbLi.querySelector('button.thumbnail');
        if (btn) btn.setAttribute('aria-current', 'true');
      }
    }
  }

  function wireExistingThumbnails(sectionId) {
    var thumbList = document.getElementById('Slider-Thumbnails-' + sectionId);
    if (!thumbList) return;
    thumbList.querySelectorAll('[data-target]').forEach(function (li) {
      var target = li.getAttribute('data-target');
      if (target !== sectionId + '-' + METAFIELD_MEDIA_ID) return;
      var btn = li.querySelector('button');
      if (btn) btn.addEventListener('click', function () {
        activateSlide(sectionId, target, li);
      });
    });
  }

  /**
   * Reorder a gallery list so that slides matching selectedPanel come first,
   * then neutral slides, then opposite-panel slides (hidden).
   * Inserts metafieldEl at position 2.
   * Returns the first visible slide element.
   */
  function reorderList(list, selectedPanel, metafieldEl) {
    if (!list) return null;

    var all = Array.prototype.slice.call(
      list.querySelectorAll('.slider__slide:not([data-custom-metafield])')
    );
    if (!all.length) return null;

    var match = [], neutral = [], opposite = [];
    all.forEach(function (s) {
      var kw = getPanelKw(s);
      if      (!kw)                                    neutral.push(s);
      else if (!selectedPanel)                         neutral.push(s);
      else if (kw === selectedPanel)                   match.push(s);
      else                                             opposite.push(s);
    });

    match.forEach(show); neutral.forEach(show); opposite.forEach(hide);

    // Build ordered sequence
    // Case A: panel images exist for the selection
    //   [match[0], metafield, match[1..n], neutrals, opposites]
    // Case B: no matching panel images (fallback)
    //   [all[0], metafield, all[1..n]]   (neutrals & opposites already in all)
    var ordered = [];

    if (match.length > 0) {
      ordered.push(match[0]);                                           // pos 1
      if (metafieldEl) ordered.push(metafieldEl);                       // pos 2
      for (var i = 1; i < match.length; i++) ordered.push(match[i]);   // pos 3+
      neutral.forEach(function (s)  { ordered.push(s); });             // always visible
      opposite.forEach(function (s) { ordered.push(s); });             // hidden
    } else {
      // Fallback: put first real slide at pos 1, metafield at pos 2, rest in original order
      // Do NOT separately push neutrals/opposites — they are already inside `all`
      ordered.push(all[0]);
      if (metafieldEl) ordered.push(metafieldEl);
      for (var j = 1; j < all.length; j++) ordered.push(all[j]);
    }

    // Deduplicate (safety net)
    var seen = [], deduped = [];
    ordered.forEach(function (el) {
      if (seen.indexOf(el) === -1) { seen.push(el); deduped.push(el); }
    });

    // Re-append in order (appendChild moves existing nodes — no cloning)
    deduped.forEach(function (el) { list.appendChild(el); });

    return deduped[0] || null;
  }

  function updateModalLists(sectionId, selectedPanel, hasMetafieldImg, featuredMediaId) {
    var modal = document.getElementById('ProductModal-' + sectionId);
    if (!modal) return;

    var modalList = modal.querySelector('#Slider-ProductModal-' + sectionId);
    var modalThumbList = modal.querySelector('#Slider-Thumbnails-Modal-' + sectionId);

    // The injected metafield slide/thumb (if any) already exists in DOM; pass it to reorderList
    var metaSlide = document.getElementById(METAFIELD_MODAL_SLIDE_ID);
    var metaThumb = document.getElementById(METAFIELD_MODAL_THUMB_ID);

    function normImgUrl(u) {
      if (!u) return '';
      try {
        var url = new URL(u, window.location.origin);
        // Shopify image_url adds width as query param; strip it for comparisons.
        url.searchParams.delete('width');
        url.searchParams.delete('height');
        var qs = url.searchParams.toString();
        return url.pathname + (qs ? '?' + qs : '');
      } catch (e) {
        return String(u).replace(/([?&])width=\d+/g, '$1').replace(/[?&]height=\d+/g, '$1');
      }
    }

    if (modalList) {
      reorderList(modalList, selectedPanel, metaSlide);

      // Hide the Liquid featured slide if it duplicates the injected metafield image
      if (featuredMediaId) {
        var featuredModalSlide = modalList.querySelector('[data-modal-slide][data-media-id="' + featuredMediaId + '"]');
        if (featuredModalSlide) featuredModalSlide.style.display = hasMetafieldImg ? 'none' : '';
      }
    }

    if (modalThumbList) {
      reorderList(modalThumbList, selectedPanel, metaThumb);

      // When metafield thumb is injected, hide any duplicate thumb(s) that point to the same image.
      // This mirrors the "hide featured media thumb when injected" logic used for the main gallery.
      if (hasMetafieldImg && metaThumb) {
        var metaImg = metaThumb.querySelector('img');
        var metaSrc = metaImg ? normImgUrl(metaImg.currentSrc || metaImg.src) : '';

        if (metaSrc) {
          Array.prototype.slice.call(modalThumbList.querySelectorAll('.thumbnail-list__item.slider__slide')).forEach(function (li) {
            if (li === metaThumb) return;
            if (li.getAttribute('data-custom-metafield') === 'true') return;
            var img = li.querySelector('img');
            if (!img) return;
            var src = normImgUrl(img.currentSrc || img.src);
            if (src && src === metaSrc) li.style.display = 'none';
          });
        }
      }

      // Additional safeguard: if the injected metafield image duplicates the variant's featured media,
      // hide that featured thumbnail by media-id as well.
      if (hasMetafieldImg && featuredMediaId) {
        var featuredModalThumb = modalThumbList.querySelector('[data-media-id="' + featuredMediaId + '"]');
        if (featuredModalThumb && featuredModalThumb !== metaThumb) featuredModalThumb.style.display = 'none';
      }
    }

    // Recalc slider offsets after reordering/hiding
    var modalViewer = document.getElementById('ProductModalViewer-' + sectionId);
    if (modalViewer && typeof modalViewer.resetPages === 'function') modalViewer.resetPages();
    var modalThumbs = document.getElementById('ProductModalThumbnails-' + sectionId);
    if (modalThumbs && typeof modalThumbs.resetPages === 'function') modalThumbs.resetPages();
  }

  /* ───────────────────────────────────────────────────────
     VARIANT DATA HELPERS
  ─────────────────────────────────────────────────────── */

  /**
   * Search window.__galleryData for the FIRST available variant where
   * every non-null entry in `constraints` matches the variant's option
   * at that position (comparison is normalised / case-insensitive).
   *
   * @param  {Array<string|null>} constraints  e.g. ["3-Panels","24x18",null]
   * @returns {string[]|null}  [option1,option2,option3] or null
   */
  function findFirstMatching(constraints) {
    var data = window.__galleryData;
    if (!data) return null;

    var allIds    = data.allVariantIds    || [];
    var varOpts   = data.variantOptions   || {};
    var avail     = data.variantAvailable || {};

    for (var i = 0; i < allIds.length; i++) {
      var id = String(allIds[i]);
      if (!avail[id]) continue;
      var info = varOpts[id];
      if (!info) continue;

      var v = [info.option1, info.option2, info.option3];
      var ok = true;
      for (var j = 0; j < constraints.length; j++) {
        if (constraints[j] == null) continue;          // null = "don't care"
        if (norm(v[j] || '') !== norm(constraints[j])) { ok = false; break; }
      }
      if (ok) return [info.option1, info.option2, info.option3];
    }
    return null;
  }

  /**
   * Cascading variant resolver.
   *
   * Given fixed upstream constraints and preferred downstream values,
   * finds the best available variant by progressively relaxing the
   * LAST downstream constraint first.
   *
   * @param {string[]} upstream    Constraints for positions 0..changedIdx
   * @param {Array}    downstream  Preferred values for positions changedIdx+1..end
   *                               (null entries are treated as "no preference")
   * @returns {string[]|null}
   */
  function resolveVariantCascade(upstream, downstream) {
    var n = downstream.length;

    for (var relaxCount = 0; relaxCount <= n; relaxCount++) {
      // Build constraint array: all upstream values +
      // the first (n - relaxCount) downstream values, rest = null
      var constraints = upstream.slice();
      for (var d = 0; d < n; d++) {
        constraints.push(d < n - relaxCount ? downstream[d] : null);
      }
      var result = findFirstMatching(constraints);
      if (result) return result;
    }

    return null;
  }

  /* ───────────────────────────────────────────────────────
     CAPTURE-PHASE INTERCEPTOR
     ───────────────────────────────────────────────────────
     Fires before Dawn's VariantSelects bubble-phase listener.

     On every option change inside <variant-selects>:
       1. Identify the changed option's fieldset index.
       2. Collect upstream constraints (0 … changedIdx).
       3. Collect current downstream values (changedIdx+1 … end).
       4. If the full combo is already available → exit, nothing to do.
       5. Call resolveVariantCascade to find the best valid combo.
       6. Update downstream radio / select inputs in-place.

     VariantSelects then reads ALL checked inputs and sends ONE AJAX
     request with the fully resolved, always-available combination.
     Price, URL, Add to Cart, and gallery all update in one round-trip.
  ─────────────────────────────────────────────────────── */

  function initVariantInterceptor() {
    document.addEventListener('change', function (e) {
      if (_selectingAvailable) return;

      var t = e.target;
      if (!t) return;

      // Must originate inside a <variant-selects> element
      var variantEl = ancestorByTag(t, 'variant-selects');
      if (!variantEl) return;

      if (!window.__galleryData) return;

      var fieldsets = Array.prototype.slice.call(variantEl.querySelectorAll('fieldset'));
      var useRadios = fieldsets.length > 0;

      // ── Identify which option position changed and its new value ──
      var changedIdx   = -1;
      var changedValue = '';

      if (t.tagName === 'INPUT' && t.type === 'radio' && useRadios) {
        changedValue = t.value || '';
        var fs = ancestorByTag(t, 'fieldset');
        if (fs) changedIdx = fieldsets.indexOf(fs);

      } else if (t.tagName === 'SELECT' && !useRadios) {
        var allSels = Array.prototype.slice.call(
          variantEl.querySelectorAll('select.select__select')
        );
        changedIdx   = allSels.indexOf(t);
        changedValue = t.value || '';
      }

      if (changedIdx === -1 || !changedValue) return;

      // ── Build upstream constraints (positions 0 … changedIdx) ──
      var upstream = [];
      if (useRadios) {
        for (var i = 0; i <= changedIdx; i++) {
          if (i === changedIdx) {
            upstream[i] = changedValue;
          } else {
            var chk = fieldsets[i].querySelector('input[type="radio"]:checked');
            upstream[i] = chk ? chk.value : null;
          }
        }
      } else {
        var sels = Array.prototype.slice.call(variantEl.querySelectorAll('select.select__select'));
        for (var i = 0; i <= changedIdx; i++) {
          upstream[i] = (i === changedIdx) ? changedValue : (sels[i].value || null);
        }
      }

      // ── Collect current downstream values (positions changedIdx+1 … end) ──
      var downstream = [];
      if (useRadios) {
        for (var i = changedIdx + 1; i < fieldsets.length; i++) {
          var chk2 = fieldsets[i].querySelector('input[type="radio"]:checked');
          downstream.push(chk2 ? chk2.value : null);
        }
      } else {
        var sels2 = Array.prototype.slice.call(variantEl.querySelectorAll('select.select__select'));
        for (var i = changedIdx + 1; i < sels2.length; i++) {
          downstream.push(sels2[i].value || null);
        }
      }

      // No downstream options → nothing to cascade; let Dawn handle
      if (downstream.length === 0) return;

      // ── Early exit: full combo is already available ──
      if (findFirstMatching(upstream.concat(downstream))) return;

      // ── Resolve best available combo via cascading fallback ──
      var bestOpts = resolveVariantCascade(upstream, downstream);
      if (!bestOpts) return; // No available variant for this upstream — let Dawn show "Unavailable"

      // ── Update downstream inputs in-place ──
      // Setting .checked programmatically does NOT fire a change event,
      // so VariantSelects will read the updated state when its listener
      // fires (bubble phase, after we return from capture phase).
      if (useRadios) {
        for (var i = changedIdx + 1; i < fieldsets.length; i++) {
          var optVal = bestOpts[i];
          if (optVal == null) continue;
          var inputs = fieldsets[i].querySelectorAll('input[type="radio"]');
          for (var k = 0; k < inputs.length; k++) {
            inputs[k].checked = (norm(inputs[k].value) === norm(optVal));
          }
        }
      } else {
        var sels3 = Array.prototype.slice.call(variantEl.querySelectorAll('select.select__select'));
        for (var i = changedIdx + 1; i < sels3.length; i++) {
          var optVal = bestOpts[i];
          if (optVal != null) sels3[i].value = optVal;
        }
      }

      // VariantSelects' bubble-phase listener now reads:
      //   fieldset[changedIdx]    → value the user just clicked (untouched)
      //   fieldset[changedIdx+1…] → corrected values from cascade (updated above)
      //
      // → selectedOptionValues = [panelId, sizeId, depthId] (all valid)
      // → single AJAX request  → available variant resolved by Shopify
      // → price + URL + Add-to-Cart + gallery all update in one round-trip ✓

    }, true); // { capture: true } — fires before VariantSelects' bubble listener
  }

  /* ───────────────────────────────────────────────────────
     FALLBACK: PROGRAMMATIC AUTO-SELECTION
     ───────────────────────────────────────────────────────
     Called only when the interceptor could not prevent an unavailable
     variant from being resolved — e.g. Dawn returns a known out-of-stock
     variant ID, or an AJAX race leaves the hidden input empty.

     Reads the current panel from the UI as the primary constraint,
     then uses the same cascade logic to find the best available combo.
  ─────────────────────────────────────────────────────── */

  /** Return the panel keyword currently selected in the UI, or null. */
  function getCurrentPanelFromUI() {
    var variantEl = document.querySelector('variant-selects');
    if (!variantEl) return null;

    var radios = variantEl.querySelectorAll('input[type="radio"]:checked');
    for (var r = 0; r < radios.length; r++) {
      var p = detectPanel(radios[r].value || '');
      if (p) return p;
    }

    var selects = variantEl.querySelectorAll('select.select__select');
    for (var s = 0; s < selects.length; s++) {
      var ps = detectPanel(selects[s].value || '');
      if (ps) return ps;
    }

    return null;
  }

  /**
   * Write bestOpts to the variant picker inputs and dispatch a change
   * event on the last input so Dawn's VariantSelects triggers an AJAX
   * fetch for the new combo.
   */
  function applyOptionsToUI(bestOpts) {
    var variantEl = document.querySelector('variant-selects');
    if (!variantEl) return;

    var fieldsets = variantEl.querySelectorAll('fieldset');
    var selects   = variantEl.querySelectorAll('select.select__select');

    if (fieldsets.length > 0) {
      // Button / radio picker
      fieldsets.forEach(function (fs, i) {
        if (bestOpts[i] == null) return;
        var inputs = fs.querySelectorAll('input[type="radio"]');
        inputs.forEach(function (inp) {
          inp.checked = (norm(inp.value) === norm(bestOpts[i]));
        });
      });
      // Dispatch change on last fieldset's checked radio to trigger VariantSelects
      var lastFs = fieldsets[fieldsets.length - 1];
      if (lastFs) {
        var lastChk = lastFs.querySelector('input[type="radio"]:checked');
        if (lastChk) lastChk.dispatchEvent(new Event('change', { bubbles: true }));
      }

    } else if (selects.length > 0) {
      // Dropdown picker
      selects.forEach(function (sel, i) {
        if (bestOpts[i] != null) sel.value = bestOpts[i];
      });
      selects[selects.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function selectFirstAvailableVariant() {
    if (_selectingAvailable) return;

    // Use current panel as primary constraint; no downstream preference
    // (downstream was unavailable — let cascade pick the first valid combo)
    var currentPanel = getCurrentPanelFromUI();
    var bestOpts     = resolveVariantCascade(
      currentPanel ? [currentPanel] : [],
      []
    );
    if (!bestOpts) return; // No available variants at all

    _selectingAvailable = true;
    applyOptionsToUI(bestOpts);
    setTimeout(function () { _selectingAvailable = false; }, 300);
  }

  /* ───────────────────────────────────────────────────────
     MAIN GALLERY UPDATE
  ─────────────────────────────────────────────────────── */

  function updateGallery(variantId) {
    var data = window.__galleryData;
    if (!data) return;

    var sectionId = getSectionId();
    if (!sectionId) return;

    variantId = String(variantId);

    // Known out-of-stock variant → trigger fallback and leave gallery as-is
    var avail = data.variantAvailable || {};
    if (avail.hasOwnProperty(variantId) && !avail[variantId]) {
      selectFirstAvailableVariant();
      return;
    }

    // Determine selected panel from the variant's options
    var info = (data.variantOptions || {})[variantId];
    var opts = info ? [info.option1, info.option2, info.option3].filter(Boolean) : [];

    var selectedPanel = null;
    for (var i = 0; i < opts.length; i++) {
      var kw = detectPanel(opts[i]);
      if (kw) { selectedPanel = kw; break; }
    }

    // Rebuild metafield slide
    removeInjected();

    var imgs     = (data.variantImages || {})[variantId] || [];
    var altText  = opts.join(' / ');
    var mainEl   = imgs.length ? buildMainSlide(sectionId, imgs[0], altText)  : null;
    var thumbEl  = imgs.length ? buildThumbSlide(sectionId, imgs[0], altText) : null;

    // Inject matching image into the product modal so the lightbox can display it.
    if (imgs.length) injectModalImage(sectionId, imgs[0], altText);

    // Reorder main gallery
    var mainList   = document.getElementById('Slider-Gallery-'    + sectionId);
    var firstSlide = reorderList(mainList, selectedPanel, mainEl);

    // The Liquid-rendered featured_media slide and the injected metafield slide
    // can show the same image (variant's featured image = custom_variant_image).
    // On desktop the main gallery shows one slide at a time so the duplicate is
    // invisible, but on mobile all slides render as a scrollable strip → 11 items.
    // Hide the Liquid slide when a metafield image is injected; restore it when not.
    var featuredMediaIds = data.variantFeaturedMediaIds || {};
    var featuredMediaId  = featuredMediaIds[variantId];
    if (featuredMediaId && mainList) {
      var featuredSlide = document.getElementById('Slide-' + sectionId + '-' + featuredMediaId);
      if (featuredSlide) {
        featuredSlide.style.display = imgs.length ? 'none' : '';
      }
    }

    // Apply the same panel show/hide/reorder logic to the modal viewer + modal thumbnails
    updateModalLists(sectionId, selectedPanel, imgs.length > 0, featuredMediaId);

    // Reorder thumbnail strip (thumbnail layout only)
    var thumbList  = document.getElementById('Slider-Thumbnails-' + sectionId);
    if (thumbList) {
      reorderList(thumbList, selectedPanel, thumbEl);
      // reorderList calls show() (clears inline style) on neutral slides, which lets
      // CSS decide visibility.  On mobile, the CSS rule that hides
      // .thumbnail-list_item--variant may not apply, so the featured-media thumbnail
      // (same image as the injected custom variant thumb) stays visible → duplicate.
      // Force-hide all variant-image thumbnails via inline style on every update.
      thumbList.querySelectorAll('.thumbnail-list_item--variant').forEach(function (li) {
        li.style.display = 'none';
      });
      // Additional safeguard for mobile: hide the featured-media thumbnail whenever
      // a metafield image is injected (it is the same image as the custom variant
      // thumb).  This covers the case where hide_variants is disabled or the
      // thumbnail-list_item--variant class is absent, so the CSS-only hide on
      // desktop does not apply at smaller viewports.
      thumbList.querySelectorAll('[data-featured-media-thumb]').forEach(function (li) {
        li.style.display = imgs.length ? 'none' : '';
      });
    }

    // Recalculate sliderItemOffset now that the custom slide is in the DOM.
    // Without this, SliderComponent uses a stale offset (2× slide width)
    // because the injected slide was not present when it first measured.
    var galleryViewer = document.getElementById('GalleryViewer-' + sectionId);
    if (galleryViewer && typeof galleryViewer.resetPages === 'function') {
      galleryViewer.resetPages();
    }
    var galleryThumbs = document.getElementById('GalleryThumbnails-' + sectionId);
    if (galleryThumbs && typeof galleryThumbs.resetPages === 'function') {
      galleryThumbs.resetPages();
    }

    wireExistingThumbnails(sectionId);

    // Activate first slide via Dawn's MediaGallery API
    var galleryEl = document.querySelector('media-gallery[id^="MediaGallery-"]');
    if (firstSlide && galleryEl) {
      var firstMediaId = firstSlide.getAttribute('data-media-id');
      if (firstMediaId) {
        if (typeof galleryEl.setActiveMedia === 'function') {
          galleryEl.setActiveMedia(firstMediaId, false);
        } else if (window.customElements && typeof customElements.whenDefined === 'function') {
          customElements.whenDefined('media-gallery').then(function () {
            if (typeof galleryEl.setActiveMedia === 'function') {
              galleryEl.setActiveMedia(firstMediaId, false);
            }
          });
        }
      }
    }
  }

  /* ───────────────────────────────────────────────────────
     EVENT WIRING
     Covers all Dawn variant-change patterns across versions.
  ─────────────────────────────────────────────────────── */

  function bindVariantChange() {

    // 1. Dawn 9+ — custom DOM event on document
    document.addEventListener('variant:change', function (e) {
      var v = e.detail && e.detail.variant;
      if (v) scheduleGalleryUpdate(v.id);
    });

    // 2. Dawn 8 — custom DOM event on product-info element
    document.querySelectorAll('product-info').forEach(function (el) {
      el.addEventListener('variant:change', function (e) {
        var v = e.detail && e.detail.variant;
        if (v) scheduleGalleryUpdate(v.id);
      });
    });

    // 3. Dawn v15 (primary) — input[name="id"] updated after every
    //    section-render AJAX fetch by product-info.js:updateVariantInputs().
    //    value = variant ID  → valid variant resolved
    //    value = ""          → no variant matched; Dawn called setUnavailable()
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.name !== 'id') return;
      if (!t.closest('form[action*="/cart/add"]')) return;

      if (t.value) {
        scheduleGalleryUpdate(t.value);
      } else if (!_selectingAvailable) {
        // Edge case: interceptor couldn't prevent an invalid combo.
        // Fallback to first available variant within the current panel.
        selectFirstAvailableVariant();
      }
    });

    // 4. Older Dawn safety net — read hidden input 50 ms after any
    //    option change (covers patterns where #3 may not fire).
    //    Uses scheduleGalleryUpdate so it collapses with listener #3
    //    when both fire for the same interaction.
    document.querySelectorAll('variant-selects, variant-radios').forEach(function (el) {
      el.addEventListener('change', function () {
        setTimeout(function () {
          var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
          if (input && input.value) scheduleGalleryUpdate(input.value);
        }, 50);
      });
    });
  }

  /* ───────────────────────────────────────────────────────
     INIT
  ─────────────────────────────────────────────────────── */

  function init() {
    if (!window.__galleryData) return;

    window.__variantGalleryUpdate = scheduleGalleryUpdate;

    // Install capture-phase interceptor (must run before bindVariantChange)
    initVariantInterceptor();

    // Initialise gallery for the variant already selected on page load
    // Use direct call (not debounced) since this is a single synchronous init
    if (window.__galleryData.selectedVariantId) {
      updateGallery(window.__galleryData.selectedVariantId);
    }

    bindVariantChange();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();